import { Injectable } from '@angular/core';
import {
  AuthorizationServiceConfiguration,
  AuthorizationServiceConfigurationJson,
  Requestor,
  StorageBackend,
  TokenResponse,
  StringMap,
  BasicQueryStringUtils
} from '@openid/appauth';
import { AuthActionBuilder, AuthActions, AuthService, Browser, EndSessionRequest, EndSessionRequestJson } from 'ionic-appauth';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { KeyrockIonicEndSessionHandler } from './KeyrockIonicEndSesison';
import environmentConfigs from '../../assets/environmentConfigs.json';
import { GlobalStateQuery } from '../state/global.appstate.query';
import { EnvironmentConfig } from '../model/environment-config';
import { Platform } from '@ionic/angular';
import { GlobalStateStore } from '../state/global.appstate.store';
const TOKEN_RESPONSE_KEY = 'token_response';
const SERVER_NAME_KEY = 'server_name';
const AUTH_CONFIG_KEY = 'auth_onfig';
const DEFAULT_EXPIRES_IN = 3600;  // Expiration time in seconds when nothing is provided by server.
const REFRESH_BUFFER = 300; // How many seconds before expiration to initiate refresh.

@Injectable({
  providedIn: 'root'
})
export class CustomAuthService extends AuthService {

  private refreshTimeout: number;
  public refreshInProgress: BehaviorSubject<boolean>; // true if refresh is running, false otherwise

  private tokenSubject: BehaviorSubject<TokenResponse>;
  public token: Observable<TokenResponse>;
  envConfig$: BehaviorSubject<EnvironmentConfig>;


  get configuration(): Promise<AuthorizationServiceConfiguration> {

    let host = this.authConfig.server_host;
    return this.storage.getItem(AUTH_CONFIG_KEY).then(conf => {
      if (conf) {
        const parsedconf = JSON.parse(conf);
        this.authConfig = parsedconf;
        host = this.authConfig.server_host;
      }

      const json: AuthorizationServiceConfigurationJson = {
        authorization_endpoint: host + '/oauth2/authorize',
        token_endpoint: host + '/oauth2/token',
        revocation_endpoint: host + '/oauth2/revoke',
        userinfo_endpoint: host + '/user',
        end_session_endpoint: host + '/auth/external_logout'
      };
      const config = new AuthorizationServiceConfiguration(json);
      return config;
    });
    //return new Promise<AuthorizationServiceConfiguration>(resolve => resolve(config));
  }

  constructor(browser?: Browser, storage?: StorageBackend, requestor?: Requestor, private state?: GlobalStateQuery, private stateStore?: GlobalStateStore, private platform?: Platform) {
    super(browser, storage, requestor);


    this.refreshInProgress = new BehaviorSubject<boolean>(false);
    this.tokenSubject = new BehaviorSubject<TokenResponse>(this.session.token);
    this.token = this.tokenSubject.asObservable();
    this.endSessionHandler = new KeyrockIonicEndSessionHandler(browser, new BasicQueryStringUtils(), this);
    this.envConfig$ = new BehaviorSubject<EnvironmentConfig>(null);

    // Set up auto-refresh.
    this.addActionListener(authEvent => {
      // Disable next token auto-refresh if user has logged out.
      if (authEvent.action === AuthActions.SignOutSuccess) {
        this.tokenSubject.next(null);
        return;
      }

      // Auto-refresh for the following event types only.
      if (
        authEvent.action !== AuthActions.SignInSuccess &&
        authEvent.action !== AuthActions.RefreshSuccess &&
        authEvent.action !== AuthActions.LoadTokenFromStorageSuccess
      ) { return; }

      // Signal refresh completion.
      if (authEvent.action === AuthActions.RefreshSuccess) {
        console.log('[Auth] Token refresh succeeded.');
      }
      if (this.envConfig$?.getValue() == null) {
        this.tokenSubject.next(null);
        return;
      }
       this.updateAuthConfig(this.envConfig$.getValue());

      this.tokenSubject.next(authEvent.tokenResponse);
    });
  }

  protected async performEndSessionRequest(): Promise<void> {
    if (this.session.token != null) {
      const requestJson: EndSessionRequestJson = {
        postLogoutRedirectURI: this.authConfig.end_session_redirect_url,
        idTokenHint: this.session.token?.idToken || ''
      };
      const request: EndSessionRequest = new EndSessionRequest(requestJson);
      const returnedUrl: string | undefined = await this.endSessionHandler.performEndSessionRequest(await this.configuration, request);

     // await this.removeAuthConfig();
      if (returnedUrl != null) {
        // Callback may come from showWindow or via another method.
        this.browser.closeWindow();
      }
      else {
        this.endSessionCallback();
      }
    } else {
      // If user has no token, they should not be logged in in the first place.
      this.endSessionCallback();
    }
  }

  protected async internalEndSessionCallback() {
    try {
      await this.storage.removeItem(TOKEN_RESPONSE_KEY);
      await this.storage.removeItem(AUTH_CONFIG_KEY);
      this.envConfig$.next(null);
    }
    catch (err) {
      console.error('Could not remove token', err);
    }
    this.notifyActionListers(AuthActionBuilder.SignOutSuccess());
  }

  async clearToken(){
    try {
      await this.storage.removeItem(TOKEN_RESPONSE_KEY);
    }
    catch (err) {
      console.error('Could not remove token', err);
    }
  }


  protected async internalLoadTokenFromStorage() {
    let token: TokenResponse | undefined;
    const tokenResponseString: string | null = await this.storage.getItem(TOKEN_RESPONSE_KEY);

    await this.loadServerNameFromStorageAndUpdateInAkita();

    if (tokenResponseString != null) {
      token = new TokenResponse(JSON.parse(tokenResponseString));
      return this.notifyActionListers(AuthActionBuilder.LoadTokenFromStorageSuccess(token));
    }
    throw new Error('No Token In Storage');
  }

  async updateAuthConfig(selectedAuthConfig: EnvironmentConfig) {

    if (selectedAuthConfig) {
      this.authConfig = selectedAuthConfig.auth_config;

      if (!this.platform.is('cordova')) {
        this.authConfig.redirect_url = window.location.origin + '/callback';
        this.authConfig.end_session_redirect_url = window.location.origin + '/endsession';
      }
      this.envConfig$.next(selectedAuthConfig);

      await this.storage.setItem(SERVER_NAME_KEY, selectedAuthConfig.configName);
      await this.storage.setItem(AUTH_CONFIG_KEY, JSON.stringify(selectedAuthConfig.auth_config));
    }

  }
  getAllEnvironmentConfigs(): EnvironmentConfig[] {
    return environmentConfigs.configs;
  }

  getEnvConfigForServer(serverName: string) {
    return environmentConfigs.configs.find(conf => conf.configName === serverName);
  }

  async loadServerNameFromStorageAndUpdateInAkita() {
    const serverName = await this.storage.getItem(SERVER_NAME_KEY);
    if (serverName != null) {
      const conf = this.getEnvConfigForServer(serverName);
      this.envConfig$.next(conf);
      this.stateStore?.updateSelectedServerName(serverName);
    }
  }
  async loadAuthFromStorage() {
    const conf = await this.storage.getItem(AUTH_CONFIG_KEY);
    if (conf) {
      const parsedconf = JSON.parse(conf);
      this.authConfig = parsedconf;
    }
  }
  async removeAuthConfig() {
    await this.storage.removeItem(AUTH_CONFIG_KEY);
  }

  public async customSignIn(authExtras) {
    await this.requestCustomAccessToken(authExtras).catch((response) => {
      this.notifyActionListers(AuthActionBuilder.SignInFailed(response));
    });
  }

  protected async requestCustomAccessToken(authExtras: StringMap): Promise<void> {

    const params = {
      grant_type: 'password',
      username: authExtras.username,
      password: authExtras.password,
      client_id: this.authConfig.client_id,
      scope: this.authConfig.scopes
    };

    const queryStr = Object.keys(params).map((el) => (`${encodeURIComponent(el)}=${encodeURIComponent(params[el])}`)).join('&');
    const resp = await this.requestor.xhr<any>({
      url: (await this.configuration).tokenEndpoint,
      method: 'POST',
      dataType: 'json',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      data: queryStr
    });

    const token = new TokenResponse(resp);
    this.session.token = token;
    this.session.isAuthenticated = true;
    await this.storage.setItem(TOKEN_RESPONSE_KEY, JSON.stringify(token.toJson()));
    this.tokenSubject.next(token);
    this.notifyActionListers(AuthActionBuilder.SignInSuccess(token));

  }


}

